import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { matchAPI } from '../lib/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar, 
  MapPin, 
  Users, 
  ChevronRight,
  Plus,
  Search,
  Clock,
  Filter
} from 'lucide-react';
import { formatCurrency, formatDate, formatTime, getMatchStatusBadge } from '../lib/utils';

export default function Matches() {
  const [matches, setMatches] = useState([]);
  const [myMatches, setMyMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');

  useEffect(() => {
    const fetchMatches = async () => {
      try {
        const [allRes, myRes] = await Promise.all([
          matchAPI.getAll({ status: 'open' }),
          matchAPI.getMy(),
        ]);
        setMatches(allRes.data || []);
        setMyMatches(myRes.data || []);
      } catch (error) {
        console.error('Failed to fetch matches:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMatches();
  }, []);

  const filteredMatches = matches.filter(match =>
    match.field_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    match.field_location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredMyMatches = myMatches.filter(match =>
    match.field_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    match.field_location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const MatchCard = ({ match }) => (
    <Link to={`/matches/${match.id}`} data-testid={`match-card-${match.id}`}>
      <Card className="hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1 border-slate-200">
        <CardContent className="p-3 sm:p-5">
          <div className="flex justify-between items-start mb-3 sm:mb-4">
            <Badge className={`${getMatchStatusBadge(match.status).className} text-[10px] sm:text-xs`}>
              {getMatchStatusBadge(match.status).text}
            </Badge>
            <span className="font-bold text-tunis-teal text-sm sm:text-base">
              {formatCurrency(match.price_per_player)}
            </span>
          </div>
          
          <h3 className="font-display font-bold text-base sm:text-lg text-tunis-blue mb-2 sm:mb-3 truncate">
            {match.field_name}
          </h3>
          
          <div className="space-y-1.5 sm:space-y-2 text-xs sm:text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Calendar className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="truncate">{formatDate(match.date)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span>{formatTime(match.time)} ({match.duration_hours}h)</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className="truncate">{match.field_location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
              <span className={match.current_players >= match.max_players ? 'text-amber-600' : ''}>
                {match.current_players}/{match.max_players} Spieler
              </span>
            </div>
          </div>

          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-slate-100 flex items-center justify-between">
            <div className="text-xs sm:text-sm text-muted-foreground truncate mr-2">
              Host: <span className="font-medium text-tunis-blue">{match.host_name}</span>
            </div>
            <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400 flex-shrink-0" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  if (loading) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
          <Skeleton className="h-6 sm:h-8 w-32 sm:w-48 mb-4 sm:mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-56 sm:h-64 rounded-xl sm:rounded-2xl" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8 pb-24" data-testid="matches-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-4 sm:mb-8">
          <div>
            <h1 className="font-display font-bold text-xl sm:text-2xl md:text-3xl text-tunis-blue">
              Spiele
            </h1>
            <p className="text-sm sm:text-base text-muted-foreground mt-1">Finde und erstelle Fußballspiele</p>
          </div>
          <Link to="/matches/create" className="w-full sm:w-auto">
            <Button className="btn-primary w-full sm:w-auto" data-testid="create-match-btn">
              <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Spiel erstellen
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="relative mb-4 sm:mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-muted-foreground" />
          <Input
            placeholder="Spiel suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 sm:pl-10 h-10 sm:h-12 text-sm sm:text-base"
            data-testid="match-search"
          />
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4 sm:mb-6 w-full sm:w-auto">
            <TabsTrigger value="all" data-testid="tab-all" className="flex-1 sm:flex-none text-xs sm:text-sm">
              Alle Spiele ({matches.length})
            </TabsTrigger>
            <TabsTrigger value="my" data-testid="tab-my" className="flex-1 sm:flex-none text-xs sm:text-sm">
              Meine Spiele ({myMatches.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="all">
            {filteredMatches.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {filteredMatches.map((match) => (
                  <MatchCard key={match.id} match={match} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-8 sm:py-12 text-center">
                  <Calendar className="w-12 h-12 sm:w-16 sm:h-16 text-slate-300 mx-auto mb-3 sm:mb-4" />
                  <h3 className="font-semibold text-base sm:text-lg text-tunis-blue mb-2">
                    Keine offenen Spiele
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground mb-4">
                    Erstelle das erste Spiel und lade Spieler ein!
                  </p>
                  <Link to="/matches/create">
                    <Button className="btn-primary">
                      <Plus className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                      Spiel erstellen
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="my">
            {filteredMyMatches.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {filteredMyMatches.map((match) => (
                  <MatchCard key={match.id} match={match} />
                ))}
              </div>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="font-semibold text-lg text-tunis-blue mb-2">
                    Noch keine Spiele
                  </h3>
                  <p className="text-muted-foreground mb-4">
                    Du hast noch an keinem Spiel teilgenommen.
                  </p>
                  <Link to="/matches">
                    <Button className="btn-primary" onClick={() => setActiveTab('all')}>
                      Spiele durchsuchen
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
